import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { aiUsageApi } from "@/lib/api";
import { toDateStr } from "@/utils/format";
import { AiConsumptionSummaryCards } from "@/pages/AiConsumption/AiConsumptionSummaryCards";
import { AiConsumptionBreakdowns } from "@/pages/AiConsumption/AiConsumptionBreakdowns";
import { AiConsumptionFilters as FilterBar } from "@/pages/AiConsumption/AiConsumptionFilters";
import { AiConsumptionTable } from "@/pages/AiConsumption/AiConsumptionTable";
import { AiConsumptionCharts } from "@/pages/AiConsumption/AiConsumptionCharts";
import { Tabs, type TabItem } from "@/components/Tabs";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/Button";
import { z } from "zod";
import { useStoredPreference } from "@/hooks/useStoredPreference";
import type {
    AiUsageItem,
    AiUsageStatsData,
    AiUsageFilters,
} from "@/pages/AiConsumption/types";

const filterSchema = z.object({
    period: z.enum(["all", "today", "week", "month"]),
    user_ids: z.array(z.string()),
    providers: z.array(z.string()),
    models: z.array(z.string()),
    context_types: z.array(z.string()),
    search: z.string().max(500).default(""),
});

export const AiConsumptionPage: React.FC = () => {
    const { t } = useTranslation("global");

    const [filters, setFilters] = useStoredPreference<AiUsageFilters>(
        "vigepra_ai_filters",
        filterSchema,
        {
            period: "all",
            user_ids: [],
            providers: [],
            models: [],
            context_types: [],
            search: "",
        },
        true
    );

    const [stats, setStats] = useState<AiUsageStatsData | null>(null);
    const [items, setItems] = useState<AiUsageItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useState<string>("");
    const { tab } = useParams<{ tab: string }>();
    const navigate = useNavigate();

    const viewMode = tab === "charts" ? "charts" : "data";

    const handleTabChange = (newTab: "data" | "charts") => {
        navigate(`/ai/${newTab}`);
    };

    const viewTabs: TabItem<"data" | "charts">[] = [
        { id: "data", label: t("ai_consumption.tab_data") },
        { id: "charts", label: t("ai_consumption.tab_charts") },
    ];

    const buildQueryParams = useCallback(
        (
            currentFilters: AiUsageFilters,
            searchVal: string
        ): Record<string, string> => {
            const params: Record<string, string> = {};

            const now = new Date();
            if (currentFilters.period === "today") {
                params.date_from = toDateStr(now);
                params.date_to = toDateStr(now);
            } else if (currentFilters.period === "week") {
                const past = new Date();
                past.setDate(now.getDate() - 7);
                params.date_from = toDateStr(past);
                params.date_to = toDateStr(now);
            } else if (currentFilters.period === "month") {
                const past = new Date();
                past.setDate(now.getDate() - 30);
                params.date_from = toDateStr(past);
                params.date_to = toDateStr(now);
            }

            if (currentFilters.user_ids && currentFilters.user_ids.length > 0) {
                params.user_ids = currentFilters.user_ids.join(",");
            }
            if (
                currentFilters.providers &&
                currentFilters.providers.length > 0
            ) {
                params.providers = currentFilters.providers.join(",");
            }
            if (currentFilters.models && currentFilters.models.length > 0) {
                params.models = currentFilters.models.join(",");
            }
            if (
                currentFilters.context_types &&
                currentFilters.context_types.length > 0
            ) {
                params.context_types = currentFilters.context_types.join(",");
            }
            if (currentFilters.search && currentFilters.search.trim()) {
                params.search = currentFilters.search.trim();
            }

            return params;
        },
        []
    );

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = buildQueryParams(filters);
            const [statsRes, listRes] = await Promise.all([
                aiUsageApi.stats(params),
                aiUsageApi.list({ ...params, per_page: 100 }),
            ]);

            if (statsRes.result && statsRes.data) {
                setStats(statsRes.data);
            }
            if (listRes.result && listRes.data) {
                const records = Array.isArray(listRes.data)
                    ? listRes.data
                    : listRes.data.data || [];
                setItems(records);
            }
        } catch (error) {
            console.error("Failed to load AI usage data:", error);
        } finally {
            setLoading(false);
        }
    }, [filters, buildQueryParams]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchData();
        }, 400);
        return () => clearTimeout(timer);
    }, [fetchData]);

    const usersList = useMemo(() => {
        if (!stats) return [];
        return stats.by_user
            .filter((u) => u.user_id !== null)
            .map((u) => ({ id: u.user_id as number, name: u.name }));
    }, [stats]);

    const providersList = useMemo(() => {
        if (!stats) return [];
        return stats.by_provider.map((p) => p.provider);
    }, [stats]);

    const modelsList = useMemo(() => {
        if (!stats) return [];
        return stats.by_model.map((m) => m.model);
    }, [stats]);

    const contextTypesList = useMemo(() => {
        if (!stats) return [];
        return stats.by_context_type.map((c) => c.context_type);
    }, [stats]);

    const defaultTotals = {
        requests: 0,
        input_tokens: 0,
        output_tokens: 0,
        total_tokens: 0,
        cost: 0,
    };

    return (
        <div className="page">
            {/* Header */}
            <PageHeader
                compact
                title={t("ai_consumption.title")}
                action={
                    <Button
                        variant="outline"
                        size="sm"
                        icon="refresh"
                        onClick={() => void fetchData()}
                        disabled={loading}
                    >
                        {t("common.refresh")}
                    </Button>
                }
            />

            {/* View Tabs */}
            <div className="mb-4 mt-4">
                <Tabs<"data" | "charts">
                    activeTab={viewMode}
                    onChange={handleTabChange}
                    variant="underline"
                    size="md"
                    syncUrl={false}
                    items={viewTabs}
                />
            </div>

            {/* Filters */}
            {viewMode === "charts" && (
                <FilterBar
                    filters={filters}
                    onFilterChange={setFilters}
                    users={usersList}
                    providers={providersList}
                    models={modelsList}
                    contextTypes={contextTypesList}
                />
            )}

            <div className="relative min-h-[400px] mt-4">
                {loading && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50 dark:bg-[#09090b]/50 backdrop-blur-[2px] rounded-2xl transition-all duration-200">
                        <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#1f1f23] rounded-full shadow-lg border border-slate-200 dark:border-white/10">
                            <Icon
                                value="reload"
                                size={16}
                                className="animate-spin text-primary"
                            />
                            <span className="text-sm font-semibold text-slate-700 dark:text-zinc-300">
                                {t("common.loading")}
                            </span>
                        </div>
                    </div>
                )}

                <div
                    className={`space-y-6 ${loading ? "opacity-40 pointer-events-none transition-opacity duration-200" : "transition-opacity duration-200"}`}
                >
                    {viewMode === "data" ? (
                        <>
                            {/* KPI Summary Cards */}
                            <AiConsumptionSummaryCards
                                totals={stats?.totals || defaultTotals}
                            />

                            {/* Visual Breakdowns */}
                            {stats && <AiConsumptionBreakdowns stats={stats} />}

                            {/* Detailed Table */}
                            <AiConsumptionTable
                                items={items}
                                filters={filters}
                                onFilters={setFilters}
                                filterControls={
                                    <FilterBar
                                        filters={filters}
                                        onFilterChange={setFilters}
                                        users={usersList}
                                        providers={providersList}
                                        models={modelsList}
                                        contextTypes={contextTypesList}
                                    />
                                }
                                loading={
                                    false
                                } /* Disabled here since we use the global backdrop */
                            />
                        </>
                    ) : stats ? (
                        <AiConsumptionCharts stats={stats} />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default AiConsumptionPage;
